import {
  Button,
  DialogBody,
  DialogFooter,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import clsx from "clsx"
import isArray from "lodash/isArray"
import { useEffect, useState } from "react"

import { API } from "@/api"
import { APIError } from "@/api/utils"
import { useClubId } from "@/lib/hooks/use-club-id"
import { mapErrors } from "@/lib/map-errors"
import { pluralize } from "@/lib/utils"

import { UploadedTeacherData } from "./constants"

const ReviewState = ({
  sheetData,
  onSubmit,
  onUploadAgain,
  files,
  // alreadyAddedTeachers,
}: {
  sheetData: UploadedTeacherData[]
  onSubmit: () => void
  onUploadAgain: () => void
  files: string | FileList | null
  setFiles: (f: string | FileList | null) => void
  // alreadyAddedTeachers?: OrganizationUserType[]
}) => {
  type RowErrors = Record<string, string[] | Record<string, string[]>>
  const [fieldErrors, setFieldErrors] = useState<RowErrors[]>([])
  const orgId = useClubId()

  const hasFieldError = (rowIndex: number, fieldName: string): boolean => {
    const rowErrors = fieldErrors[rowIndex]
    if (!rowErrors) return false
    return fieldName in rowErrors
  }

  const hasRowError = (rowIndex: number): boolean => {
    const rowErrors = fieldErrors[rowIndex]
    return rowErrors && Object.keys(rowErrors).length > 0
  }

  const getFieldErrorMessage = (
    rowIndex: number,
    fieldName: string,
  ): string | null => {
    const rowErrors = fieldErrors[rowIndex]
    if (!rowErrors || !(fieldName in rowErrors)) return null

    const fieldError = rowErrors[fieldName]
    if (Array.isArray(fieldError)) {
      return fieldError.join(", ")
    } else if (typeof fieldError === "object") {
      // Handle nested errors (e.g., {"0": ["error message"]})
      const nestedErrors = Object.values(fieldError).flat()
      return nestedErrors.join(", ")
    }
    return null
  }

  // TODO: Need to get email id
  // const newTeachers = sheetData.filter((i) =>
  //   alreadyAddedTeachers?.find((j) => j.name !== i.email),
  // )
  // const commonTeachers = sheetData.filter((i) =>
  //   alreadyAddedTeachers?.find((j) => j.name === i.email),
  // )
  const bulkAddTeacher = useMutation({
    mutationFn: (data: FileList) => {
      const formData = new FormData()

      formData.append("file", data[0])
      formData.append("organization", orgId)
      formData.append("source", "suraasa_kind")

      return API.teachers.bulkAddTeacher({
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    },
    onSuccess() {
      toast.success("Teachers added successfully")
      onSubmit()
    },
    onError(error) {
      mapErrors(error)
      if (error instanceof APIError) {
        if (
          isArray(error.errors.fieldErrors) &&
          error.errors.fieldErrors.length > 0
        ) {
          const mappedErrors = error.errors.fieldErrors.map((rowError) => {
            return Object.fromEntries(Object.entries(rowError)) as Record<
              string,
              string[] | Record<string, string[]>
            >
          })

          setFieldErrors(mappedErrors)
        }
      }
    },
  })

  useEffect(() => {
    return () => {
      setFieldErrors([])
    }
  }, [])

  return (
    <>
      <DialogBody className="p-2.5">
        <div>
          <Typography variant="strong" className="mb-2.5 text-onSurface-900">
            {pluralize("contact", sheetData.length)} found
          </Typography>
          {/* {commonTeachers.length > 0 ? (
            <Typography className="mb-2 text-onSurface-700">
              You’re about to add{" "}
              <span className="font-semibold">
                {pluralize("new teacher", newTeachers.length)}
              </span>{" "}
              to this Growthspace.
            </Typography>
          ) : (
            <Typography className="mb-2 text-onSurface-700">
              You’re inviting {pluralize("teacher", sheetData.length)} to this
              Growthspace. Are you sure you want to continue?
            </Typography>
          )} */}
          <Typography className="mb-2 text-onSurface-700">
            You’re inviting {pluralize("teacher", sheetData.length)} to this
            Growthspace. Teachers who are already part of it will be ignored.
          </Typography>
          {fieldErrors.some((row) => Object.keys(row).length > 0) && (
            <div className="mb-2 rounded-md bg-critical-100 p-2">
              <Typography className="text-critical-700">
                The highlighted records contain missing data. Kindly check the
                sheet and{" "}
                <button onClick={onUploadAgain} className="text-primary-500">
                  try again.
                </button>
              </Typography>
            </div>
          )}

          <div className="max-h-[490px] overflow-auto rounded-xl bg-surface-100">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-[1] bg-surface-100">
                <tr className="border-b border-surface-300">
                  <th className="min-w-[180px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Name
                    </Typography>
                  </th>
                  <th className="min-w-[200px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Email Address
                    </Typography>
                  </th>
                  <th className="min-w-[150px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Phone Number
                    </Typography>
                  </th>
                  {/* <th className="min-w-[180px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Subjects Taught
                    </Typography>
                  </th> */}
                  {/* <th className="min-w-[150px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Grades Taught
                    </Typography>
                  </th> */}
                  {/* <th className="min-w-[150px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Boards
                    </Typography>
                  </th> */}
                  {/* <th className="min-w-[180px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Teaching Levels
                    </Typography>
                  </th> */}
                  {/* <th className="min-w-[150px] p-2 text-start">
                    <Typography
                      variant="strongSmallBody"
                      className="text-start"
                    >
                      Designation
                    </Typography>
                  </th> */}
                </tr>
              </thead>
              <tbody>
                {sheetData.map((teacher, index) => (
                  <tr
                    key={index}
                    className={clsx(
                      "border-b border-surface-300 last:border-b-0",
                      {
                        "bg-critical-100": hasRowError(index),
                      },
                    )}
                  >
                    <td
                      className={clsx("min-w-[180px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "name",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.name}
                      </Typography>
                      {hasFieldError(index, "name") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "name")}
                        </Typography>
                      )}
                    </td>
                    <td
                      className={clsx("min-w-[200px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "email",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.email}
                      </Typography>
                      {hasFieldError(index, "email") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "email")}
                        </Typography>
                      )}
                    </td>
                    <td
                      className={clsx("min-w-[200px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "phoneNumber",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.phoneNumber || "-"}
                      </Typography>
                      {hasFieldError(index, "phoneNumber") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "phoneNumber")}
                        </Typography>
                      )}
                    </td>
                    {/* <td
                      className={clsx("min-w-[180px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "subjectsTaught",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.subjectsTaught || "-"}
                      </Typography>
                      {hasFieldError(index, "subjectsTaught") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "subjectsTaught")}
                        </Typography>
                      )}
                    </td> */}
                    {/* <td
                      className={clsx("min-w-[150px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "gradesTaught",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.gradesTaught || "-"}
                      </Typography>
                      {hasFieldError(index, "gradesTaught") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "gradesTaught")}
                        </Typography>
                      )}
                    </td> */}
                    {/* <td
                      className={clsx("min-w-[150px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "boards",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.boards || "-"}
                      </Typography>
                      {hasFieldError(index, "boards") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "boards")}
                        </Typography>
                      )}
                    </td> */}
                    {/* <td
                      className={clsx("min-w-[180px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "teachingLevels",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.teachingLevels || "-"}
                      </Typography>
                      {hasFieldError(index, "teachingLevels") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "teachingLevels")}
                        </Typography>
                      )}
                    </td> */}
                    {/* <td
                      className={clsx("min-w-[150px] p-2", {
                        "border-2 border-critical-600": hasFieldError(
                          index,
                          "designation",
                        ),
                      })}
                    >
                      <Typography
                        variant="smallBody"
                        className="break-all text-onSurface-700"
                      >
                        {teacher.designation || "-"}
                      </Typography>
                      {hasFieldError(index, "designation") && (
                        <Typography
                          variant="smallBody"
                          className="mt-1 text-critical-600"
                        >
                          {getFieldErrorMessage(index, "designation")}
                        </Typography>
                      )}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button
          color={null}
          onClick={onUploadAgain}
          size="sm"
          disabled={bulkAddTeacher.isPending}
          className="rounded-[10px] border border-onSurface-300 p-1 text-onSurface-700"
        >
          Upload Again
        </Button>
        <Button
          onClick={() => {
            if (files instanceof FileList) bulkAddTeacher.mutate(files)
          }}
          color="black"
          loading={bulkAddTeacher.isPending}
          size="sm"
          className="rounded-xl"
        >
          Add Teachers
        </Button>
      </DialogFooter>
    </>
  )
}

export default ReviewState
